import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import NoResultsFound from 'src/components/ui/NoResultsFound/NoResultsFound';
import WarningBox, { WarningBoxColorType } from 'src/components/ui/WarningBox/WarningBox';
import {
  SelectedProductsProduct,
  SelectProductsFromInventory,
} from 'src/components/layouts/SelectProducts/SelectProducts';
import { OverlaySectionHeadline } from 'src/components/layouts/OverlaySectionHeadline/OverlaySectionHeadline';
import { useProductBomsQuery, useBoms, useBomLines, BomLine } from 'src/services/boms';
import BomLinesOverview, {
  BomWorkingLine,
} from 'src/components/layouts/ProductOverlay/BomLinesOverview/BomLinesOverview';

const bomLineToWorkingLine = (line: BomLine): BomWorkingLine => ({
  lineId: line.id ?? null,
  product_id: line.product_id as number,
  quantity: (line.quantity as number) ?? 1,
  name: line.name,
  article_number: line.article_number,
  barcode: line.barcode,
});

// Quantity must be at least 1; an emptied input is stored as 0 locally so it
// stays editable, but is never sent to the backend below 1.
const normalizeQuantity = (quantity: number): number => Math.max(1, quantity || 1);

export interface BomComponentsRef {
  saveChanges: () => Promise<void>;
  createNewBomLines: (productId: number) => Promise<void>;
  isSelectingProducts: boolean;
  selectedProducts: BomWorkingLine[];
}

interface BomComponentsProps {
  productId: number;
  editable?: boolean;
  hasProductSetEnabled?: boolean;
}

const BomComponents = forwardRef<BomComponentsRef, BomComponentsProps>(
  ({ productId, editable = false, hasProductSetEnabled }, ref) => {
    const { t } = useTranslation();
    const [showProductSelect, setShowProductSelect] = useState(false);

    const { data: productBoms } = useProductBomsQuery(productId);
    const { createBomMutation } = useBoms(productId);
    const bomId = productBoms?.[0]?.id;
    const { bomLinesQuery, createBomLineMutation, updateBomLineMutation, deleteBomLineMutation } = useBomLines(
      bomId ?? 0,
    );

    // Single source of truth for the BOM tab during an edit session. Adding,
    // removing and quantity changes mutate this state only — nothing is sent to
    // the backend until saveChanges() runs (triggered by the overlay's Save
    // button), so the tab behaves like the rest of the form.
    const [workingLines, setWorkingLines] = useState<BomWorkingLine[]>([]);
    const hasLoadedRef = useRef(false);

    // (Re)sync the working set from the server while NOT editing, so an
    // in-progress edit is never silently overwritten by a background refetch.
    // Skipped in the create flow (productId 0) where there is no server state
    // and the working set is built up purely locally.
    useEffect(() => {
      if (!productId) return;
      if (editable && hasLoadedRef.current) return;
      setWorkingLines((bomLinesQuery.data ?? []).map(bomLineToWorkingLine));
      if (bomLinesQuery.data) hasLoadedRef.current = true;
    }, [bomLinesQuery.data, editable, productId]);

    // Persist all staged add / remove / quantity changes against the diff of the
    // server state. Creates the BOM on the fly if the product has none yet.
    const persistWorkingLines = async (targetProductId: number) => {
      const original = bomLinesQuery.data ?? [];
      const toCreate = workingLines.filter(line => line.lineId == null);
      const toDelete = original.filter(line => !workingLines.some(working => working.lineId === line.id));
      const toUpdate = workingLines.filter(
        line => line.lineId != null && original.find(o => o.id === line.lineId)?.quantity !== line.quantity,
      );

      if (toCreate.length === 0 && toDelete.length === 0 && toUpdate.length === 0) return;

      let targetBomId = bomId;
      if (!targetBomId && toCreate.length > 0) {
        const response = await createBomMutation.mutateAsync({
          params: { path: { product_id: targetProductId } },
          body: {},
        });
        if (!response.id) throw new Error('Failed to create BOM');
        targetBomId = response.id;
      }

      await Promise.all([
        ...toCreate.map(line =>
          createBomLineMutation.mutateAsync({
            params: { path: { bom_id: targetBomId as number } },
            body: { product_id: line.product_id, quantity: normalizeQuantity(line.quantity) },
          }),
        ),
        ...toUpdate.map(line =>
          updateBomLineMutation.mutateAsync({
            params: { path: { line_id: line.lineId as number } },
            body: { product_id: line.product_id, quantity: normalizeQuantity(line.quantity) },
          }),
        ),
        ...toDelete.map(line =>
          deleteBomLineMutation.mutateAsync({
            params: { path: { line_id: line.id as number } },
          }),
        ),
      ]);
    };

    useImperativeHandle(
      ref,
      () => ({
        // Edit flow: persist staged changes for an already existing product.
        saveChanges: async () => {
          await persistWorkingLines(productId);
        },
        // Create flow: the product id only exists after creation, so the BOM and
        // all its lines are created here in one go.
        createNewBomLines: async (newProductId: number) => {
          if (workingLines.length === 0) return;
          const response = await createBomMutation.mutateAsync({
            params: { path: { product_id: newProductId } },
            body: {},
          });
          if (!response.id) throw new Error('Failed to create BOM');
          await Promise.all(
            workingLines.map(line =>
              createBomLineMutation.mutateAsync({
                params: { path: { bom_id: response.id as number } },
                body: { product_id: line.product_id, quantity: normalizeQuantity(line.quantity) },
              }),
            ),
          );
        },
        isSelectingProducts: showProductSelect,
        selectedProducts: workingLines,
      }),
      [
        workingLines,
        bomLinesQuery.data,
        bomId,
        productId,
        showProductSelect,
        createBomMutation,
        createBomLineMutation,
        updateBomLineMutation,
        deleteBomLineMutation,
      ],
    );

    const onSelectProducts = (products: SelectedProductsProduct[]) => {
      setWorkingLines(prev => {
        const existingProductIds = new Set(prev.map(line => line.product_id));
        const additions = products
          .filter(product => !existingProductIds.has(product.product_id))
          .map<BomWorkingLine>(product => ({
            lineId: null,
            product_id: product.product_id,
            quantity: product.quantity ?? 1,
            name: product.name,
            article_number: product.article_number,
            barcode: product.barcode,
          }));
        return [...prev, ...additions];
      });
      setShowProductSelect(false);
    };

    const onQuantityChange = (changedProductId: number, quantity: number) => {
      setWorkingLines(prev => prev.map(line => (line.product_id === changedProductId ? { ...line, quantity } : line)));
    };

    const onRemove = (removedProductId: number) => {
      setWorkingLines(prev => prev.filter(line => line.product_id !== removedProductId));
    };

    return showProductSelect ? (
      <SelectProductsFromInventory
        hideProduct={productId}
        onUpdateSuccess={onSelectProducts}
        defaultValue={[]}
        excludeBomComponents
      />
    ) : (
      <>
        <OverlaySectionHeadline>{t('general.is_product_set')}</OverlaySectionHeadline>
        {!hasProductSetEnabled && (
          <WarningBox
            colorType={WarningBoxColorType.Warning}
            className="mb-40"
            title={t('general.product_set_disabled')}
            copy={t('general.product_set_disabled_copy')}
          />
        )}
        <BomLinesOverview
          lines={workingLines}
          editable={editable}
          onQuantityChange={onQuantityChange}
          onRemove={onRemove}
        />
        {!editable && workingLines.length === 0 && <NoResultsFound smallTitle title={t('general.no_products_found')} />}
        {editable && (
          <Button
            tone={ButtonToneProps.SECONDARY}
            icon="plus"
            title={t('general.add_product')}
            onClick={() => setShowProductSelect(true)}
          />
        )}
      </>
    );
  },
);

export default BomComponents;
